import { useEffect, useMemo } from "react"
import { useMutation } from "@tanstack/react-query"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import { likeCruise } from "../../../services/firebase-functions/likeCruise"
import ThumbsUpIcon from "../../assets/icons/ThumbsUpIcon"
import styles from "./styles.module.scss"
import classNames from "classnames"
import { colors } from "@theme/colors"

type LikeButtonProps = {
  userId: string
  tripId: string
  tripSearchId: string
  docId: string
  cruiseLikes: string[]
  testId: string
  onClick?: () => void
}

class LikeButtonState {
  likes: string[]
  pressed: boolean

  constructor(likes: string[]) {
    this.likes = likes
    this.pressed = false
    makeAutoObservable(this)
  }

  setLikes(value: string[]) {
    this.likes = value
  }

  setPressed(value: boolean) {
    this.pressed = value
  }
}

const LikeButton = observer(
  ({ userId, tripId, tripSearchId, docId, cruiseLikes, testId, onClick }: LikeButtonProps) => {
    const state = useMemo(() => new LikeButtonState(cruiseLikes), [cruiseLikes])

    const doesUserLikedCruise = state.likes.includes(userId)
    const likesAmount = state.likes.length

    const { mutate: mutateLikeCruise, isPending: isCruiseLikingLoading } = useMutation({
      mutationFn: () => likeCruise(tripId, tripSearchId, docId),
    })

    const handleLikeCruise = async () => {
      state.setPressed(!state.pressed)
      const doesUserLikesCruise = cruiseLikes.includes(userId)
      state.setLikes(
        doesUserLikesCruise ? cruiseLikes.filter((id) => id !== userId) : [...cruiseLikes, userId],
      )

      mutateLikeCruise()
    }

    useEffect(() => {
      state.setLikes(cruiseLikes)
    }, [cruiseLikes])

    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          if (onClick) {
            onClick()
          } else {
            handleLikeCruise()
          }
        }}
        disabled={isCruiseLikingLoading}
        className={classNames(styles.likeButtonContainer, styles.likeButton)}
        data-testid={testId}
      >
        <>
          {doesUserLikedCruise ? (
            <ThumbsUpIcon color={colors.iconGreenAccent} />
          ) : (
            <ThumbsUpIcon color={colors.iconTertiary} />
          )}
          {likesAmount > 0 ? (
            <p
              className={classNames(
                styles.likesAmount,
                doesUserLikedCruise && styles.likesAmountIsLiked,
              )}
            >
              {likesAmount}
            </p>
          ) : null}
        </>
      </button>
    )
  },
)

export default LikeButton
